// A discount on one case's aligner price: a number plus a "% / PKR" switch. Used beside every tray
// count input (new case, case editor, setup share, tray prompt) so the price the clinic will
// actually be charged is decided in the same breath as the count. An empty number clears it.
import { ALIGNER_DISCOUNT_TYPES, type AlignerDiscount, type AlignerDiscountType, type AlignerQuote } from '@alignova/shared';
import { inputCls } from './wfUi.tsx';

const TYPE_LABEL: Record<AlignerDiscountType, string> = { percent: '%', amount: 'PKR' };

export function DiscountInput({
  value,
  onChange,
  className = '',
  disabled = false,
}: {
  value: AlignerDiscount | null;
  onChange: (next: AlignerDiscount | null) => void;
  className?: string;
  disabled?: boolean;
}) {
  const type: AlignerDiscountType = value?.type ?? 'percent';
  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <input
        type="number"
        min={0}
        max={type === 'percent' ? 100 : undefined}
        step={type === 'percent' ? 1 : 100}
        className={`${inputCls} w-24 tabnum`}
        value={value?.value ?? ''}
        placeholder="0"
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') return onChange(null);
          const n = Math.max(0, Number(raw));
          if (!Number.isFinite(n)) return;
          onChange({ type, value: type === 'percent' ? Math.min(100, n) : n });
        }}
      />
      <select
        className={`${inputCls} w-auto`}
        value={type}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value as AlignerDiscountType;
          onChange(value ? { type: next, value: next === 'percent' ? Math.min(100, value.value) : value.value } : null);
        }}
        aria-label="Discount type"
      >
        {ALIGNER_DISCOUNT_TYPES.map((t) => (
          <option key={t} value={t}>
            {TYPE_LABEL[t]}
          </option>
        ))}
      </select>
    </div>
  );
}

/** "20 × PKR 2,500 = PKR 50,000 · less PKR 5,000 (10%) = PKR 45,000" in one line. */
export function QuoteLine({ quote, suffix }: { quote: AlignerQuote; suffix?: string }) {
  const g = quote.gross;
  return (
    <span className="tabnum">
      {g.trays} × PKR {g.perTrayRupees.toLocaleString()} = PKR {g.totalRupees.toLocaleString()}
      {quote.discountPaisa > 0 && (
        <>
          {' '}
          · less PKR {quote.discountRupees.toLocaleString()}
          {quote.discount?.type === 'percent' ? ` (${quote.discount.value}%)` : ''} ={' '}
          <b className="text-ink">PKR {quote.netRupees.toLocaleString()}</b>
        </>
      )}
      {suffix ? ` ${suffix}` : ''}
    </span>
  );
}
